import { fetchAuthSession } from 'aws-amplify/auth';
import { Configuration } from 'we-tree-api-client/v2';
import appConfig from '@/config/app';

// Cache for API configuration with token refresh handling
let cachedConfiguration: Configuration | undefined;
let lastTokenFetch = 0;
const TOKEN_CACHE_DURATION = 50 * 60 * 1000; // 50 minutes (tokens typically expire at 60 min)

/**
 * Gets API configuration with cached token management.
 * The configuration is cached and tokens are refreshed automatically by AWS Amplify.
 * We invalidate the cache every 50 minutes to ensure we get refreshed tokens.
 */
export const getApiConfiguration = async (): Promise<Configuration | undefined> => {
  try {
    const now = Date.now();
    const shouldRefresh = !cachedConfiguration || now - lastTokenFetch > TOKEN_CACHE_DURATION;

    if (shouldRefresh) {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();

      // Only cache configuration if we have a valid token
      // This prevents caching a configuration without a token, which would cause API calls to fail
      if (token) {
        cachedConfiguration = new Configuration({
          basePath: appConfig.apiUrl,
          accessToken: token
        });

        lastTokenFetch = now;
      } else {
        // If no token is available, clear the cache and return undefined
        // This forces the caller to wait for authentication to complete
        cachedConfiguration = undefined;
        lastTokenFetch = 0;
        return undefined;
      }
    }

    // If we have a cached configuration, verify it still has a token
    // This handles cases where the token might have been cleared
    if (cachedConfiguration && !cachedConfiguration.accessToken) {
      cachedConfiguration = undefined;
      lastTokenFetch = 0;
      // Try once more to get a fresh token
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (token) {
        cachedConfiguration = new Configuration({
          basePath: appConfig.apiUrl,
          accessToken: token
        });
        lastTokenFetch = now;
      } else {
        return undefined;
      }
    }

    return cachedConfiguration;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting auth token:', error);
    // Clear cache on error
    cachedConfiguration = undefined;
    lastTokenFetch = 0;
    return undefined;
  }
};

/**
 * Invalidates the cached API configuration.
 * Call this when user logs out or when you need to force a token refresh.
 */
export const invalidateApiConfiguration = (): void => {
  cachedConfiguration = undefined;
  lastTokenFetch = 0;
};

export const getAccessToken = async (): Promise<string> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();

    if (!token) {
      throw new Error('No access token available');
    }

    return token;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting access token:', error);
    throw new Error('Authentication required.');
  }
};
